"use client";
import React from "react";
import Image from "next/image";
import { LiveChatButton } from "../ui";
import { fontSizes } from "@/constants/constants";

const defaultStats = [
  { value: "500+", label: "Books Published" },
  { value: "100%", label: "Ownership & Royalties" },
  { value: "5★", label: "Author-Rated Support" },
  { value: "24/7", label: "Dedicated Guidance" },
];

const ServicesShowcase = ({
  ctaText,
  title,
  description,
  items = [],
  bottomText,
  stats = defaultStats,
  bottomHeading = "Ready to take the first step? Our team is just a message away.",
}) => {
  return (
    <section className="relative overflow-hidden bg-cream bg-paper-grain py-20 px-4 md:px-8">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="grid grid-cols-1 items-end gap-8 lg:grid-cols-12">
          <div className={ctaText ? "lg:col-span-7" : "lg:col-span-12"}>
            <span className="eyebrow flex items-center justify-center gap-3 text-gold-deep lg:justify-start">
              <span className="h-px w-8 bg-gold/70" />
              The Process
            </span>
            <h2 className={`mt-4 text-center font-display font-semibold leading-tight text-ink lg:text-left ${fontSizes.text_5_xl}`}>
              {title}
            </h2>
            {description && (
              <p className="mt-4 text-center leading-relaxed text-ink-soft lg:text-left">
                {description}
              </p>
            )}
          </div>
          {ctaText && (
            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-ink/10 bg-white/70 p-6 backdrop-blur">
                <p className="leading-relaxed text-ink-soft">{ctaText}</p>
                <div className="mt-4">
                  <LiveChatButton buttonText="Live Chat" classStyle="text-white bg-brand-600 hover:bg-brand-700 h-11" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Benefits grid */}
        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((benefit, i) => (
            <div
              key={benefit.id}
              data-aos="fade-up"
              data-aos-delay={(i % 3) * 90}
              className="group relative overflow-hidden rounded-3xl border border-ink/10 bg-white p-7 transition-all duration-400 hover:-translate-y-1.5 hover:border-gold/40 hover:shadow-editorial"
            >
              <span className="pointer-events-none absolute -right-2 -top-3 font-display text-7xl font-semibold text-ink/[0.05] transition-colors group-hover:text-gold/15">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50">
                {benefit.icon && (
                  <Image src={benefit.icon} alt={benefit.title} className="h-7 w-7 object-contain" />
                )}
              </span>
              <h3 className="relative mt-5 font-display text-xl font-semibold text-ink">
                {benefit.title}
              </h3>
              <span className="relative mt-3 block h-[3px] w-9 rounded-full bg-gold transition-all duration-300 group-hover:w-16" />
              <p className="relative mt-3 text-sm leading-relaxed text-ink-soft">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>

        {/* Stats band */}
        <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-4">
          {stats.map((stat) => (
            <div key={stat.label} className="rounded-2xl border border-ink/10 bg-white px-4 py-6 text-center shadow-sm">
              <p className="font-display text-2xl font-semibold text-brand-700 lg:text-3xl">{stat.value}</p>
              <p className="mt-1 text-xs leading-relaxed text-ink-soft lg:text-sm">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Bottom */}
        {(bottomText || bottomHeading) && (
          <div className="mx-auto mt-10 max-w-4xl text-center">
            {bottomText && (
              <p className="text-sm leading-relaxed text-ink-soft">{bottomText}</p>
            )}
            {bottomHeading && (
              <p className="mt-4 font-display text-base font-semibold text-ink lg:text-lg">
                {bottomHeading}
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default ServicesShowcase;
